import { Link } from 'react-router-dom';
import certiniteLogo from '../assets/certinite-logo-home.png';

export type CertiniteBrandVariant = 'onLight' | 'onDark';
export type CertiniteBrandSize = 'sm' | 'md' | 'lg';

const sizeStyles: Record<
  CertiniteBrandSize,
  { img: string; text: string; gap: string }
> = {
  sm: {
    img: 'h-8 w-auto',
    text: 'text-[0.8125rem] tracking-[0.24em] sm:text-sm sm:tracking-[0.26em]',
    gap: 'gap-2',
  },
  md: {
    img: 'h-9 w-auto sm:h-10',
    text: 'text-[0.95rem] tracking-[0.26em] sm:text-[1.125rem] sm:tracking-[0.28em]',
    gap: 'gap-2.5 sm:gap-3',
  },
  lg: {
    img: 'h-12 w-auto sm:h-14',
    text: 'text-lg tracking-[0.28em] sm:text-xl sm:tracking-[0.3em]',
    gap: 'gap-3',
  },
};

const wordmarkBase =
  'font-[family-name:var(--font-nav)] font-bold uppercase leading-none';

interface CertiniteBrandProps {
  variant?: CertiniteBrandVariant;
  size?: CertiniteBrandSize;
  showWordmark?: boolean;
  className?: string;
}

export default function CertiniteBrand({
  variant = 'onLight',
  size = 'md',
  showWordmark = true,
  className = '',
}: CertiniteBrandProps) {
  const s = sizeStyles[size];
  const wordmarkClass =
    variant === 'onDark'
      ? `${wordmarkBase} ${s.text} text-white/95`
      : `${wordmarkBase} ${s.text} bg-gradient-to-br from-[#2f8f4e] via-[#256b3a] to-[#1a2e22] bg-clip-text text-transparent`;

  return (
    <span className={`inline-flex items-center ${s.gap} ${className}`}>
      <img src={certiniteLogo} alt="" className={`${s.img} object-contain`} decoding="async" />
      {showWordmark && (
        <span className={wordmarkClass} aria-hidden>
          CERTINITE
        </span>
      )}
    </span>
  );
}

interface CertiniteBrandLinkProps extends CertiniteBrandProps {
  to?: string;
  onClick?: () => void;
}

export function CertiniteBrandLink({
  to = '/',
  onClick,
  className = '',
  ...brandProps
}: CertiniteBrandLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`inline-flex shrink-0 items-center py-1 transition-opacity hover:opacity-90 ${className}`}
      aria-label="Certinite"
    >
      <CertiniteBrand {...brandProps} />
    </Link>
  );
}

/** Static URL for favicon, JSON-LD, and prerendered HTML */
export const CERTINITE_LOGO_PUBLIC_PATH = '/certinite-logo.png';
